import type { Channel, Platform } from '@rp/contracts'
import { Badge, Button, EmptyState } from '../../design'
import { formatDateTime } from '../../lib/time'
import { PLATFORM_LABELS } from '../queue/labels'
import { SETTINGS_COPY } from './copy'

type ChannelListProps = {
  channels: readonly Channel[]
  connecting: Platform | null
  onReconnect: (platform: Platform) => void
  onDisconnect: (channel: Channel) => void
}

type ChannelRowProps = {
  channel: Channel
  connecting: boolean
  onReconnect: () => void
  onDisconnect: () => void
}

type ChannelHealthState = 'disconnected' | 'unsubscribed' | 'connected'

const HEALTH_BADGES: Record<ChannelHealthState, { tone: 'danger' | 'warning' | 'success'; label: string }> = {
  disconnected: { tone: 'danger', label: SETTINGS_COPY.disconnected },
  unsubscribed: { tone: 'warning', label: SETTINGS_COPY.webhookMissing },
  connected: { tone: 'success', label: SETTINGS_COPY.connected },
}

const healthOf = (channel: Channel): ChannelHealthState => {
  if (channel.status === 'disconnected') return 'disconnected'
  return channel.webhookSubscribed === false ? 'unsubscribed' : 'connected'
}

const ChannelRow = ({ channel, connecting, onReconnect, onDisconnect }: ChannelRowProps) => {
  const health = healthOf(channel)
  const down = health !== 'connected'
  const badge = HEALTH_BADGES[health]
  return (
    <li className="channel" data-down={String(down)}>
      <div className="channel-head">
        <span className="channel-name">{channel.name}</span>
        <Badge tone={badge.tone}>{badge.label}</Badge>
      </div>
      {health === 'unsubscribed' ? <p className="channel-hint">{SETTINGS_COPY.webhookMissingHint}</p> : null}
      <div className="channel-meta">
        <span>{PLATFORM_LABELS[channel.platform]}</span>
        <span className="num">
          {channel.lastCheckedAt === null
            ? SETTINGS_COPY.neverChecked
            : SETTINGS_COPY.lastChecked(formatDateTime(new Date(channel.lastCheckedAt)))}
        </span>
      </div>
      <div className="channel-actions">
        {down ? (
          <Button variant="primary" pending={connecting} pendingLabel={SETTINGS_COPY.reconnecting} onClick={onReconnect}>
            {SETTINGS_COPY.reconnect}
          </Button>
        ) : null}
        <Button variant="danger" onClick={onDisconnect}>
          {SETTINGS_COPY.disconnect}
        </Button>
      </div>
    </li>
  )
}

export const ChannelList = ({ channels, connecting, onReconnect, onDisconnect }: ChannelListProps) => {
  if (channels.length === 0) {
    return <EmptyState title={SETTINGS_COPY.channelsEmpty} description={SETTINGS_COPY.channelsEmptyBody} />
  }
  return (
    <ul className="channel-list" aria-label={SETTINGS_COPY.channels}>
      {channels.map((channel) => (
        <ChannelRow
          key={channel.id}
          channel={channel}
          connecting={connecting === channel.platform}
          onReconnect={() => onReconnect(channel.platform)}
          onDisconnect={() => onDisconnect(channel)}
        />
      ))}
    </ul>
  )
}
